import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogActions, Button, Alert,
  TextField, MenuItem, CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import { Encabezado, Seccion, Rejilla, fieldSx, contenidoSx, pieSx, botonPrincipalSx } from "./dialogoUi";

const PROCEDENCIA_CI = ["LP", "CB", "SC", "BE", "PA", "TJ", "PT", "CH", "OR"];
const DEPARTAMENTOS = ["La Paz", "Oruro", "Cochabamba", "Santa Cruz", "Beni", "Pando", "Tarija", "Potosi", "Chuquisaca"];

// Igual que formatFecha en el resto del módulo: una fecha legacy '0000-00-00'
// llega como 30/11/1899 — se trata como vacía, no se precarga el input con basura.
const isoFecha = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1950) return "";
  return String(value).slice(0, 10);
};

const camposIniciales = (empleado) => ({
  nombre: empleado?.nombre || "",
  paterno: empleado?.paterno || "",
  materno: empleado?.materno || "",
  ci: empleado?.ci || "",
  procedencia_ci: empleado?.procedencia_ci || "LP",
  titulo: empleado?.titulo || "",
  correo: empleado?.correo || "",
  telefono: empleado?.telefono || "",
  celular: empleado?.celular || "",
  direccion: empleado?.direccion || "",
  departamento: empleado?.departamento || "La Paz",
  fecha_cumple: isoFecha(empleado?.fecha_cumple),
  fecha_pas: isoFecha(empleado?.fecha_pas),
  fecha_con: isoFecha(empleado?.fecha_con),
  fecha_afi: isoFecha(empleado?.fecha_afi),
  fecha_ini: isoFecha(empleado?.contrato?.fecha_ini),
  cargo: empleado?.cargo || "",
  contacto: empleado?.contacto || "",
  observaciones: empleado?.observaciones || "",
  nro_cuenta: empleado?.nro_cuenta || "",
  caja: empleado?.caja || "",
  codigo_asegurado: empleado?.codigo_asegurado || "",
  nua: empleado?.nua || "",
});

const EditarEmpleadoDialog = ({ open, onClose, empleado, onGuardado }) => {
  const [form, setForm] = useState(() => camposIniciales(empleado));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(camposIniciales(empleado));
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const handleGuardar = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.patch(`/api/administracion-personal/empleado/${empleado.id}`, form);
      onGuardado();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  const campo = (clave, label, extra = {}) => (
    <TextField size="small" label={label} value={form[clave]} onChange={handleChange(clave)} sx={fieldSx} {...extra} />
  );
  const fecha = (clave, label) => campo(clave, label, { type: "date", InputLabelProps: { shrink: true } });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <Encabezado titulo="Modificar datos" subtitulo={[empleado?.nombre, empleado?.paterno, empleado?.materno].filter(Boolean).join(" ")} />
      <DialogContent sx={contenidoSx}>
        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

        <Seccion titulo="Datos personales">
          <Rejilla>
            {campo("nombre", "Nombre")}
            {campo("paterno", "Ap. paterno")}
            {campo("materno", "Ap. materno")}
            {campo("ci", "Carnet de identidad")}
            <TextField select size="small" label="Procedencia CI" value={form.procedencia_ci} onChange={handleChange("procedencia_ci")} sx={fieldSx}>
              {PROCEDENCIA_CI.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            {campo("titulo", "Título")}
            {fecha("fecha_cumple", "Fecha de cumpleaños")}
          </Rejilla>
        </Seccion>

        <Seccion titulo="Contacto">
          <Rejilla>
            {campo("correo", "Correo electrónico")}
            {campo("celular", "Celular")}
            {campo("telefono", "Teléfono")}
            {campo("direccion", "Dirección", { sx: { ...fieldSx, gridColumn: { sm: "span 2" } } })}
            <TextField select size="small" label="Departamento" value={form.departamento} onChange={handleChange("departamento")} sx={fieldSx}>
              {DEPARTAMENTOS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Rejilla>
        </Seccion>

        <Seccion titulo="Datos laborales">
          <Rejilla>
            {campo("cargo", "Cargo")}
            {fecha("fecha_pas", "Inicio de pasantía")}
            {fecha("fecha_con", "Fecha de contrato")}
            {fecha("fecha_afi", "Fecha de afiliación")}
            {fecha("fecha_ini", "Inicio (contrato vigente)")}
          </Rejilla>
        </Seccion>

        <Seccion titulo="Cuenta y seguro">
          <Rejilla cols={4}>
            {campo("nro_cuenta", "Nro. de cuenta")}
            {campo("caja", "Caja")}
            {campo("codigo_asegurado", "Código del asegurado")}
            {campo("nua", "NUA")}
          </Rejilla>
        </Seccion>

        <Seccion titulo="Notas">
          <Rejilla cols={1}>
            {campo("contacto", "Persona de contacto", { multiline: true, minRows: 2 })}
            {campo("observaciones", "Observaciones", { multiline: true, minRows: 2 })}
          </Rejilla>
        </Seccion>
      </DialogContent>
      <DialogActions sx={pieSx}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button variant="contained" onClick={handleGuardar} disabled={saving} sx={botonPrincipalSx}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditarEmpleadoDialog;
